/**
 * Changelog — HARDCODED einträge (bewusst: kein Backend, jeder sieht dasselbe).
 * Neuen Eintrag IMMER OBEN anfügen; `id` eindeutig lassen — das Gelesen-System
 * vergleicht die neueste id mit localStorage.
 */

export type ChangelogType = 'NEU' | 'UPDATE' | 'FIX' | 'WICHTIG';

export interface ChangelogEntry {
  id: string;
  date: string; // Anzeige, z.B. "19. August 2026"
  type: ChangelogType;
  title: string;
  summary: string;
  /** Hardcoded HTML — wird 1:1 gerendert (eigener, vertrauenswürdiger Inhalt). */
  html: string;
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    id: '2026-11-01-editor-werkzeuge-interne-kommunikation',
    date: '1. November 2026',
    type: 'UPDATE',
    title: 'Editor-Werkzeuge auch bei geöffneter Interner Kommunikation',
    summary: 'KI, Textbausteine, Anhang und Übersetzen bleiben im Antwort-Editor sichtbar, wenn rechts die Interne Kommunikation geöffnet ist – als kompakte Icons.',
    html: `
      <p>Bisher verschwanden die Werkzeuge im Antwort-Editor (<strong>KI ⌘K</strong>, <strong>Textbausteine</strong>, <strong>Anhang</strong>, <strong>Übersetzen</strong>), sobald die Interne Kommunikation geöffnet war.</p>
      <p>Jetzt richtet sich die Leiste nach dem verfügbaren Platz: Bei breitem Editor wie gewohnt, bei geöffneter Interner Kommunikation als kompakte Icons (KI als ✨-Symbol, Kürzel ⌘K funktioniert weiterhin). Nur bei sehr schmalen Fenstern werden sie ausgeblendet.</p>`,
  },
  {
    id: '2026-10-27-mcp-tickets-anlegen',
    date: '27. Oktober 2026',
    type: 'NEU',
    title: 'MCP: Tickets anlegen, Priorität und Tags ändern',
    summary: 'Über die MCP-Schnittstelle (z. B. Claude oder ein eigener Bot) lassen sich jetzt neue Tickets anlegen sowie Priorität und Tags bestehender Tickets ändern.',
    html: `
      <p>Neue Werkzeuge in der MCP-Schnittstelle:</p>
      <ul>
        <li><strong>create_ticket</strong> – legt ein Ticket für einen Fall ohne Ticket an (z. B. eine Anfrage aus dem Shop-Chat): Betreff, Kunden-E-Mail, Bestellnummer, Priorität, Tags, Organisation und optional eine Zuweisung. Die Fallbeschreibung wird als interne Notiz gespeichert. Der Kunde erhält dabei <strong>keine</strong> E-Mail.</li>
        <li><strong>update_ticket_priority</strong> und <strong>update_ticket_tags</strong> – Priorität bzw. Tags ändern.</li>
      </ul>
      <p>Status, Betreff, Zuweisung, interne Notizen, Antworten und Side-Conversations waren bereits verfügbar. Alle Änderungen erscheinen im Ticketverlauf; nutzbar für Agenten und Admins.</p>`,
  },
  {
    id: '2026-10-25-cmdk-haiku',
    date: '25. Oktober 2026',
    type: 'UPDATE',
    title: 'Cmd+K antwortet schneller',
    summary: 'Der KI-Chat (Cmd+K) nutzt jetzt Claude Haiku 5.5 und liefert Texte deutlich schneller.',
    html: `
      <p>Cmd+K läuft ohne Tag bzw. mit <strong>@claude</strong> jetzt auf Claude Haiku 5.5 statt Sonnet 5. Überarbeiten, Kürzen, Übersetzen und neue Antworten kommen dadurch spürbar schneller.</p>
      <p>Mit <strong>@chatgpt</strong> antwortet weiterhin das GPT-Modell; fällt ein Modell aus, springt wie bisher automatisch das andere ein.</p>`,
  },
  {
    id: '2026-10-23-shopify-bestellung-per-nummer',
    date: '23. Oktober 2026',
    type: 'UPDATE',
    title: 'Shopify-Bestellung wird auch über die Bestellnummer gefunden',
    summary: 'Schreibt ein Kunde von einer anderen E-Mail-Adresse als bei der Bestellung, findet das Bestellpanel die Bestellung jetzt über die Bestellnummer aus dem Ticket – auch in weiteren verbundenen Shopify-Shops.',
    html: `
      <p>Bisher suchte das Bestellpanel nur nach der E-Mail-Adresse des Absenders. Kam die Anfrage z. B. über ein Kontaktformular mit einer anderen Adresse, blieb das Panel leer – obwohl die Bestellnummer in der Nachricht stand.</p>
      <p>Jetzt wird in diesem Fall die Bestellnummer aus Betreff und Nachrichten verwendet (z. B. „Bestellnummer: #552513348“). Sind mehrere Shopify-Shops verbunden, werden alle durchsucht; Shop-Name und Link zeigen dann auf den richtigen Shop.</p>`,
  },
  {
    id: '2026-10-23-verlauf-breite-textbausteine',
    date: '23. Oktober 2026',
    type: 'FIX',
    title: 'Nachrichten nicht mehr rechts abgeschnitten, Textbausteine scrollbar',
    summary: 'Enthielt ein Ticket eine sehr breite E-Mail, wurden alle Nachrichten am rechten Rand abgeschnitten. Außerdem lässt sich die Textbaustein-Auswahl jetzt scrollen.',
    html: `
      <p><strong>Ticketverlauf:</strong> Eine einzelne breite E-Mail (z. B. mit Tabellen oder sehr langen Links) hat den ganzen Verlauf in die Breite gezogen – der Text aller Nachrichten war rechts abgeschnitten. Jetzt bricht der Text wieder um; breite Inhalte lassen sich innerhalb ihrer Nachricht seitlich scrollen.</p>
      <p><strong>Textbausteine:</strong> Die Auswahl ist in der Höhe begrenzt und scrollbar, sodass <strong>Einfügen</strong> auch bei langen Bausteinen erreichbar ist. Die Vorschau zeigt den Text formatiert statt als HTML-Quelltext und nur die ersten Zeilen.</p>`,
  },
  {
    id: '2026-10-16-sms-neue-nummer',
    date: '16. Oktober 2026',
    type: 'FIX',
    title: 'SMS-Empfang bei neu gekauften Nummern',
    summary: 'Direkt nach dem Kauf einer Rufnummer stand beim SMS-Empfang fälschlich „unterstützt keinen SMS-Empfang“. Jetzt erscheint ein Hinweis, dass die Nummer noch aktiviert wird.',
    html: `
      <p>Nach dem Kauf aktiviert unser Telefonie-Anbieter eine Rufnummer erst nach ein paar Minuten. In dieser Zeit zeigte <strong>SMS empfangen</strong> fälschlich an, die Nummer könne keine SMS empfangen – auch bei Nummern, die das können (z. B. US-Nummern).</p>
      <p>Jetzt steht dort, dass die Nummer noch aktiviert wird. Sobald sie aktiv ist, lässt sich der SMS-Empfang einschalten; <strong>Status aktualisieren</strong> prüft den Stand jederzeit neu.</p>`,
  },
  {
    id: '2026-10-15-instagram-messenger-liste',
    date: '15. Oktober 2026',
    type: 'NEU',
    title: 'Eigene Liste für Instagram & Messenger',
    summary: 'In der Seitenleiste gibt es jetzt „Instagram & Messenger“: alle Direktnachrichten aus Instagram und dem Facebook Messenger auf einen Blick, getrennt von den E-Mails.',
    html: `
      <p>Unter <strong>Instagram &amp; Messenger</strong> in der Seitenleiste stehen jetzt nur die Tickets, die über Instagram-Direktnachrichten oder den Facebook Messenger hereingekommen sind.</p>
      <p>Die Antwort schreiben Sie wie gewohnt im Ticket, sie geht direkt an den Kunden in Instagram bzw. Messenger zurück. Beitrags-Kommentare bleiben weiterhin unter <strong>Kommentare</strong>.</p>`,
  },
  {
    id: '2026-10-11-ticketnummer-detail',
    date: '11. Oktober 2026',
    type: 'UPDATE',
    title: 'Ticket-Nummer direkt im Ticket sichtbar',
    summary: 'Die Ticket-Nummer steht jetzt im Ticketdetail an der Stelle der früheren Referenz-ID und lässt sich per Klick kopieren.',
    html: `
      <p>Im Ticketdetail steht die <strong>Ticket-Nummer</strong> (z. B. TKT-2026-000139) jetzt neben Kunde und Status – dort, wo früher die Referenz-ID angezeigt wurde.</p>
      <p>Ein Klick auf die Nummer kopiert sie in die Zwischenablage. Die Suche findet Tickets weiterhin auch über die alte Referenz-ID.</p>`,
  },
  {
    id: '2026-10-08-meta-verbindungstest',
    date: '8. Oktober 2026',
    type: 'FIX',
    title: 'Messenger & Instagram: Verbindungstest richtet alles ein',
    summary: 'Der Test erkennt falsche Tokens und abonniert die Seite automatisch für Nachrichten.',
    html: `
      <p>„Verbindung testen“ unter <strong>Einstellungen → Messenger &amp; Instagram</strong> prüft jetzt, ob wirklich ein Seiten-Token eingetragen ist. Ein Benutzer-Token wird automatisch gegen das Seiten-Token getauscht. Anschließend wird die Facebook-Seite beim Webhook für Nachrichten und Kommentare abonniert. Ohne dieses Abo hat Meta keine Nachrichten geschickt, obwohl der Test erfolgreich war.</p>
      <p>Fehlen dem Token Berechtigungen (pages_messaging, pages_manage_metadata), zeigt der Test das jetzt direkt an.</p>`,
  },
  {
    id: '2026-10-07-neue-organisation-mitglied',
    date: '7. Oktober 2026',
    type: 'FIX',
    title: 'Neue Organisation: Tickets sofort sichtbar',
    summary: 'Wer eine Organisation anlegt, wird automatisch Mitglied und sieht ihre Tickets.',
    html: `
      <p>Bisher war eine neu angelegte Organisation niemandem zugeordnet. E-Mails wurden zwar abgerufen und Tickets erstellt, sie erschienen aber in keiner Liste. Jetzt wird die Person, die die Organisation anlegt, automatisch Mitglied. Weitere Mitarbeitende fügst du wie gewohnt unter <strong>Einstellungen → Benutzer</strong> hinzu.</p>`,
  },
  {
    id: '2026-10-07-ki-entwuerfe-manuell',
    date: '7. Oktober 2026',
    type: 'UPDATE',
    title: 'KI-Entwürfe: manuell beantwortete Tickets',
    summary: 'Hat das Team selbst geantwortet, fällt der Entwurf des Cloud-Agents automatisch aus der Warteschlange.',
    html: `
      <p>Antwortet jemand direkt im Ticket, statt den Entwurf zu verwenden, oder wird das Ticket geschlossen, ist der vorbereitete Entwurf überholt. Er wird jetzt automatisch als <strong>Manuell beantwortet</strong> markiert.</p>
      <ul>
        <li>Solche Entwürfe stehen grau am Ende der Liste und zählen nicht mehr als offen</li>
        <li>Ein Klick auf <strong>Verstanden</strong> entfernt den Eintrag, oben gibt es außerdem „alle entfernen“</li>
        <li>Wird der Entwurf in der Ticketansicht übernommen, zählt er weiterhin als gesendet</li>
        <li>Der Cloud-Agent erstellt keine Entwürfe mehr für Tickets, die das Team bereits beantwortet hat</li>
        <li>Alle Texte der Seite sind jetzt in allen Sprachen verfügbar</li>
      </ul>`,
  },
  {
    id: '2026-10-07-auto-antwort-formular',
    date: '7. Oktober 2026',
    type: 'FIX',
    title: 'Auto-Antwort auch für Formular-Tickets',
    summary: 'Tickets aus dem Kontaktformular (VynForm) erhalten jetzt ebenfalls die automatische Eingangsbestätigung.',
    html: `
      <p>Die Auto-Antwort wurde bisher nur bei E-Mail-Tickets verschickt. Jetzt erhalten auch Kunden, die über das Kontaktformular schreiben, die Eingangsbestätigung. Die gesendete Auto-Antwort erscheint außerdem wieder im Ticketverlauf.</p>`,
  },
  {
    id: '2026-10-05-rueckerstattung',
    date: '5. Oktober 2026',
    type: 'NEU',
    title: 'Rückerstattung & Teilerstattung direkt im Ticket',
    summary: 'Erstattungen für Shopify und alle angebundenen Shops, auch einzelne Artikel oder Beträge.',
    html: `
      <p>In den <strong>Bestelldetails</strong> eines Tickets kannst du eine Bestellung jetzt ganz oder teilweise erstatten.</p>
      <ul>
        <li><strong>Teilerstattung</strong>: einzelne Artikel und Mengen, Versandkosten oder ein freier Betrag</li>
        <li>Shopify: Erstattung direkt über den Shop, optional mit Rückbuchung in den Bestand</li>
        <li>Funktioniert für alle angebundenen Shops (JTL, Shopify, WooCommerce u. a.) bzw. über PayPal, Stripe und Klarna</li>
        <li>Verlauf aller Erstattungen mit Betrag und Status (geplant, ausgeführt, fehlgeschlagen)</li>
        <li>Bei Vorkasse/Überweisung erscheint der Hinweis „Achtung Vorkasse – manuelle Überweisung notwendig“</li>
      </ul>`,
  },
  {
    id: '2026-10-05-mcp',
    date: '5. Oktober 2026',
    type: 'UPDATE',
    title: 'MCP: mehr Zugriff für Claude & ChatGPT',
    summary: 'Cloud-Agent-Entwürfe, interne Kommunikation, eigene Ansichten und Anhänge über MCP, dazu eine zentrale Anmeldung.',
    html: `
      <p>Über die MCP-Anbindung können KI-Assistenten jetzt deutlich mehr im Desk erledigen:</p>
      <ul>
        <li>Entwürfe des Cloud-Agents ansehen, bearbeiten, senden oder ablehnen</li>
        <li>Interne Kommunikation eines Tickets lesen</li>
        <li>Deine eigenen Ansichten aus der Seitenleiste abrufen</li>
        <li>Anhänge von Tickets öffnen</li>
        <li>Zentrale Adresse <strong>https://mcp.vyndesk.com/mcp</strong>: bei der Anmeldung wird automatisch dein Desk gewählt</li>
      </ul>`,
  },
  {
    id: '2026-10-04-pdf-undo',
    date: '4. Oktober 2026',
    type: 'NEU',
    title: 'PDF-Export & Senden rückgängig machen',
    summary: 'Tickets als PDF exportieren und versendete Antworten innerhalb weniger Sekunden zurückholen.',
    html: `
      <ul>
        <li><strong>PDF-Export</strong> im Ticket: Kundenkonversation, interne Kommunikation und Notizen frei kombinierbar</li>
        <li><strong>Senden rückgängig machen</strong>: Antworten werden erst nach einer kurzen Frist verschickt und lassen sich bis dahin zurückholen. Die Frist stellst du unter <strong>Einstellungen → Senden</strong> ein.</li>
      </ul>`,
  },
  {
    id: '2026-09-24-zusammenfuehren',
    date: '24. September 2026',
    type: 'UPDATE',
    title: 'Tickets zusammenführen: zuverlässiger + Vorschläge',
    summary: 'Keine Abbrüche mehr bei einzelnen Tickets, keine Ketten, und Vorschläge für passende Tickets.',
    html: `
      <ul>
        <li>Bereits zusammengeführte Tickets werden übersprungen statt den ganzen Vorgang abzubrechen</li>
        <li>Keine verschachtelten Ketten mehr: alles landet direkt im Ziel-Ticket</li>
        <li>Im Ticket erscheinen Vorschläge für Tickets desselben Kunden, die zusammengehören könnten</li>
      </ul>`,
  },
  {
    id: '2026-09-17-performance',
    date: '17. September 2026',
    type: 'FIX',
    title: 'Desk wieder schnell & Suche nach Bestellnummer',
    summary: 'Verlangsamung bei mehreren geöffneten Tabs behoben, Suche nach Bestellnummern repariert.',
    html: `
      <ul>
        <li>Mit mehreren geöffneten Desk-Tabs konnte der Desk minutenlang langsam werden. Die Ursache ist behoben.</li>
        <li>Die Suche nach einer Bestellnummer lieferte bei manchen Desks einen Fehler statt des Tickets</li>
      </ul>`,
  },
  {
    id: '2026-09-09-ki',
    date: '9. September 2026',
    type: 'UPDATE',
    title: 'KI: neue Modelle, stabilere Antworten',
    summary: 'Cmd+K, Antwortvorschläge und Tags laufen auf neuen Modellen, Antworten ohne Fett- und Kursivschrift.',
    html: `
      <ul>
        <li>Cmd+K und Antwortvorschläge nutzen jetzt Claude Sonnet 5 mit automatischem Ausweichmodell, das beseitigt die Ausfälle der letzten Wochen</li>
        <li>Mit <strong>@chatgpt</strong> in Cmd+K antwortet gezielt das GPT-Modell</li>
        <li>KI-Antworten verwenden keine Fett- oder Kursivschrift mehr, außer du verlangst es</li>
        <li>Automatische Tags und Klassifizierung auf einem neueren Modell</li>
      </ul>`,
  },
  {
    id: '2026-09-03-sms',
    date: '3. September 2026',
    type: 'NEU',
    title: 'Telefonie: eingehende SMS',
    summary: 'SMS an deine Desk-Nummer erscheinen im Telefon-Fenster.',
    html: `
      <p>Eingehende SMS an deine Telefonnummer werden jetzt im Telefon-Popover angezeigt (nur Empfang).</p>`,
  },
  {
    id: '2026-08-30-email-fixes',
    date: '30. August 2026',
    type: 'FIX',
    title: 'E-Mail-Darstellung & Abmelden',
    summary: 'Unsichtbare Nachrichten, CSS-Text im Ticket und fehlender Abmelden-Knopf behoben.',
    html: `
      <ul>
        <li>Nachrichten, die vollständig in einem Zitat standen, waren im Ticket unsichtbar</li>
        <li>Stilangaben mancher E-Mails erschienen als Text im Ticket</li>
        <li>„Abmelden“ ist in den Einstellungen wieder vorhanden</li>
      </ul>`,
  },
  {
    id: '2026-08-25-social-kommentare',
    date: '25. August 2026',
    type: 'NEU',
    title: 'Facebook- & Instagram-Kommentare: eigener Posteingang',
    summary: 'Kommentare landen gesammelt in einem eigenen Bereich statt als einzelne Tickets.',
    html: `
      <p>Kommentare unter Facebook- und Instagram-Beiträgen erzeugen keine Ticket-Flut mehr, sondern erscheinen in einem eigenen Posteingang. Beim Antworten auf einen Kommentar wird deutlich angezeigt, dass die Antwort öffentlich ist.</p>`,
  },
  {
    id: '2026-08-25-einstellungen',
    date: '25. August 2026',
    type: 'UPDATE',
    title: 'Einstellungen neu gestaltet',
    summary: 'Neue Navigation, Seitenpanels statt Dialoge und genauere Zähler in der Seitenleiste.',
    html: `
      <ul>
        <li>Einstellungen mit neuer Navigation, Team-, Automatisierungs- und Systembereich überarbeitet</li>
        <li>Benutzer und Organisationen werden in Seitenpanels bearbeitet</li>
        <li>Formular-Einrichtung (VYN-Tag) direkt in den Einstellungen</li>
        <li>Die Ticketzähler der Seitenleiste stimmen mit der Liste überein und aktualisieren sich sofort</li>
      </ul>`,
  },
  {
    id: '2026-08-22-shops',
    date: '22. August 2026',
    type: 'NEU',
    title: 'Neue Shop-Integrationen',
    summary: 'WooCommerce, Shopware 6, Magento 2, BigCommerce, PrestaShop und İkas.',
    html: `
      <p>Neben JTL und Shopify lassen sich jetzt auch <strong>WooCommerce, Shopware 6, Magento 2, BigCommerce, PrestaShop und İkas</strong> anbinden, jeweils einfach per API-Schlüssel. Bestellungen erscheinen dann direkt im Ticket.</p>`,
  },
  {
    id: '2026-08-21-nutzung-spam',
    date: '21. August 2026',
    type: 'NEU',
    title: 'Nutzungsübersicht & Spam-Absender',
    summary: 'Kontingente in der Seitenleiste, Spam-Absender werden künftig automatisch aussortiert.',
    html: `
      <ul>
        <li>Die <strong>Nutzung</strong>-Karte in der Seitenleiste zeigt Kontingente, Restmengen und abgerechnete Mehrnutzung</li>
        <li>„Archiv“ heißt jetzt <strong>Spam</strong>. Als Spam markierte Absender landen künftig automatisch dort.</li>
      </ul>`,
  },
  {
    id: '2026-08-19-changelog',
    date: '19. August 2026',
    type: 'NEU',
    title: 'Changelog mit Gelesen-Anzeige',
    summary: 'Diese Seite — alle Updates an einem Ort, neue Einträge werden in der Sidebar markiert.',
    html: `
      <p>Ab jetzt findest du alle Neuerungen hier. Gibt es einen neuen Eintrag, pulsiert das Megafon-Symbol unten in der Sidebar, bis du die Seite öffnest.</p>
      <ul>
        <li>Einträge als kompakte Badges untereinander — Klick öffnet die Details</li>
        <li>Gelesen-Status pro Browser (kein Konto-Sync nötig)</li>
      </ul>`,
  },
  {
    id: '2026-08-18-mobile-app',
    date: '18. August 2026',
    type: 'NEU',
    title: 'Vyndesk Mobile App (Beta, iOS)',
    summary: 'Tickets, KI-Entwürfe, Dashboard und Telefon — jetzt auch unterwegs.',
    html: `
      <p>Die neue iOS-App verbindet sich per QR-Code mit deinem Desk: <strong>Einstellungen → Mobile App</strong> öffnen und den Code scannen.</p>
      <ul>
        <li>Ticket-Liste mit deinen eigenen und geteilten Ansichten</li>
        <li>Ticket-Detail: Antworten, interne Notizen, KI-Antworten, Bestellungen, Zusammenführen, Übersetzung</li>
        <li>KI-Entwürfe prüfen, überarbeiten und senden</li>
        <li>Telefon: Anrufe führen und annehmen, Aufnahmen und KI-Zusammenfassungen</li>
        <li>Cloud-Agent starten/stoppen — live synchron mit dem Web</li>
      </ul>`,
  },
  {
    id: '2026-08-17-whiteboard',
    date: '17. August 2026',
    type: 'NEU',
    title: 'Meetings: Whiteboard',
    summary: 'Gemeinsames Zeichnen und Präsentieren direkt im Meeting.',
    html: `
      <p>In jedem Meeting gibt es jetzt ein interaktives Whiteboard — über das Präsentations-Symbol in der Leiste. Auch Gäste können mitzeichnen.</p>`,
  },
  {
    id: '2026-08-16-meet-ansichten',
    date: '16. August 2026',
    type: 'UPDATE',
    title: 'Meetings: Neue Ansichten',
    summary: 'Aktiver Sprecher, Rasteransicht und Anheften einzelner Teilnehmer.',
    html: `
      <ul>
        <li><strong>Aktiver Sprecher</strong> (Standard): wer spricht, wird automatisch groß gezeigt</li>
        <li><strong>Raster</strong>: alle Teilnehmer gleichzeitig im Blick</li>
        <li><strong>Anheften</strong>: Klick auf eine Kachel fixiert die Person</li>
        <li>Bildschirmfreigaben werden automatisch angeheftet</li>
      </ul>`,
  },
  {
    id: '2026-08-15-editor-resize',
    date: '15. August 2026',
    type: 'UPDATE',
    title: 'Ticket-Detail: Editor-Höhe anpassbar',
    summary: 'Der Antwort-Editor lässt sich per Ziehen vergrößern — die Höhe bleibt gespeichert.',
    html: `
      <p>Zwischen Verlauf und Editor gibt es jetzt einen Ziehgriff. Die gewählte Höhe gilt für alle Tickets und bleibt auch nach dem Neuladen erhalten.</p>`,
  },
  {
    id: '2026-08-14-suche-bestellnummern',
    date: '14. August 2026',
    type: 'FIX',
    title: 'Suche: Bestellnummern mit #',
    summary: 'Bestellnummern werden jetzt zuverlässig gefunden — mit und ohne #.',
    html: `
      <p>Die Suche nach Bestellnummern (z.B. <code>#552510659</code>) übersieht keine Treffer mehr; auch ältere Tickets sind vollständig indexiert.</p>`,
  },
  {
    id: '2026-08-13-login',
    date: '13. August 2026',
    type: 'UPDATE',
    title: 'Neue Login-Seite',
    summary: 'Frisches Design im Stil der neuen Vyndesk-Oberfläche.',
    html: `
      <p>Die Anmeldeseite wurde neu gestaltet — klarer, schneller, mit dem gewohnten Look der Ticketverwaltung.</p>`,
  },
];

export const LATEST_CHANGELOG_ID = CHANGELOG[0]?.id ?? '';

const SEEN_KEY = 'changelog.lastSeenId';
export const CHANGELOG_SEEN_EVENT = 'changelog-seen';

export function isChangelogUnread(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) !== LATEST_CHANGELOG_ID;
  } catch {
    return false;
  }
}

export function markChangelogSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, LATEST_CHANGELOG_ID);
  } catch {
    /* private mode vs. — sessizce geç */
  }
  window.dispatchEvent(new Event(CHANGELOG_SEEN_EVENT));
}
